'use client';

import { BodyDefault, BodyStrong } from '../../components/typography';
import { Button } from '../../components/button';
import { IconButton } from '../../components/icon-button';
import { Skeleton } from '../../components/skeleton';
import { TableGrid, type TableGridRow } from '../table-grid/TableGrid';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import type { ApplicationRoleDto } from '@terra/shared-contracts-applications';
import {
  Edit03Icon,
  Add01Icon,
  Delete02Icon,
  SparklesIcon,
} from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { ContentCard } from '../content-card/ContentCard';

export type CredentialsSubTab = 'credentials' | 'customRoles';
export type CredentialsEmptyStateProps = {
  title: string;
  description: string;
  buttonText: string;
  onButtonClick: () => void;
  isButtonDisabled?: boolean;
  hideButton?: boolean;
};
export type CustomRolesContentProps = {
  customRoles: ApplicationRoleDto[];
  isLoadingRoles: boolean;
  isReadOnly?: boolean;
  minHeight?: string;
  onAddButtonClick: () => void;
  onEditRoleClick: (role: ApplicationRoleDto) => void;
  onDeleteRoleClick: (role: ApplicationRoleDto) => void;
  onAnalyzeRoleClick?: (role: ApplicationRoleDto) => void;
};

const ROLE_ANALYSIS_RUNNING_STATUSES: ReadonlySet<
  ApplicationRoleDto['analysisStatus']
> = new Set<ApplicationRoleDto['analysisStatus']>(['ANALYZING', 'PENDING']);

export const isRoleAnalysisRunning = (role: ApplicationRoleDto): boolean =>
  ROLE_ANALYSIS_RUNNING_STATUSES.has(role.analysisStatus);

export const CredentialsEmptyState = ({
  title,
  description,
  buttonText,
  onButtonClick,
  isButtonDisabled,
  hideButton = false,
}: CredentialsEmptyStateProps) => {
  return (
    <Box
      borderWidth="1px"
      borderColor="border.neutral.subtle"
      borderRadius="lg"
      p={4}
    >
      <VStack gap={2} alignItems="center">
        <BodyStrong>{title}</BodyStrong>
        <BodyDefault color="fg.neutral.muted" textAlign="center">
          {description}
        </BodyDefault>
        {!hideButton ? (
          <Button
            size="xs"
            variant="outline"
            disabled={isButtonDisabled}
            onClick={onButtonClick}
            mt={1}
          >
            {buttonText}
          </Button>
        ) : null}
      </VStack>
    </Box>
  );
};

export const CustomRolesContent = ({
  customRoles,
  isLoadingRoles,
  isReadOnly,
  minHeight,
  onAddButtonClick,
  onEditRoleClick,
  onDeleteRoleClick,
  onAnalyzeRoleClick,
}: CustomRolesContentProps) => {
  const tCredentials = useTranslations('designSystem.credentials');
  const tRoles = useTranslations('designSystem.applicationRoles');

  if (isLoadingRoles) {
    return <Skeleton height="55px" borderRadius="md" />;
  }

  if (customRoles.length === 0) {
    return (
      <CredentialsEmptyState
        title={tCredentials('customRoles.emptyStateTitle')}
        description={tCredentials('customRoles.emptyStateDescription')}
        buttonText={tCredentials('customRoles.addCustomRole')}
        onButtonClick={onAddButtonClick}
        isButtonDisabled={isReadOnly}
      />
    );
  }

  const customRoleRows = customRoles.map<TableGridRow>((role) => ({
    id: role.id,
    cells: [
      role.name,
      role.description || '-',
      <HStack key={`${role.id}-actions`} gap={1} justifyContent="flex-end">
        {onAnalyzeRoleClick ? (
          <IconButton
            size="xs"
            variant="ghost"
            aria-label={tRoles('roleCard.analyze')}
            title={tRoles('roleCard.analyze')}
            disabled={isReadOnly}
            loading={isRoleAnalysisRunning(role)}
            onClick={() => onAnalyzeRoleClick(role)}
          >
            <SparklesIcon size={14} />
          </IconButton>
        ) : null}
        <IconButton
          size="xs"
          variant="ghost"
          aria-label={tCredentials('card.menu.edit')}
          disabled={isReadOnly}
          onClick={() => onEditRoleClick(role)}
        >
          <Edit03Icon size={14} />
        </IconButton>
        <IconButton
          size="xs"
          variant="ghost"
          aria-label={tCredentials('card.menu.delete')}
          disabled={isReadOnly}
          onClick={() => onDeleteRoleClick(role)}
        >
          <Delete02Icon size={14} />
        </IconButton>
      </HStack>,
    ],
    value: `${role.name} ${role.description ?? ''}`,
  }));

  return (
    <ContentCard minHeight={minHeight}>
      <TableGrid
        columns={[
          {
            id: 'name',
            title: tCredentials('customRoles.columns.name'),
          },
          {
            id: 'description',
            title: tCredentials('customRoles.columns.description'),
          },
          {
            id: 'actions',
            title: tCredentials('customRoles.columns.actions'),
          },
        ]}
        rows={customRoleRows}
        placeholder={tCredentials('customRoles.searchPlaceholder')}
        tableMaxHeight="50vh"
        rightContent={
          isReadOnly ? null : (
            <Button size="xs" variant="outline" onClick={onAddButtonClick}>
              <Add01Icon size={16} />
              {tCredentials('customRoles.addCustomRole')}
            </Button>
          )
        }
      />
    </ContentCard>
  );
};
